import { useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowLeft, Building2, Accessibility } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api, errMsg, useFetch, label, fmtDate, money } from "../lib/api";
import { ImportSource } from "../components/ImportSource";
import { JobDetailContent } from "../components/JobDetailContent";
import { uploadResume } from "../lib/resumeUpload";
import { useAuth } from "../context/AuthContext";
import { btn, Loading, ErrorBox, SampleBadge, SaveButton, ReportButton, StatusBadge, TextArea, ErrorSummary, Badge } from "../components/kit";

export function DetailSection({ title, children, show = true }) {
  if (!show || !children) return null;
  const testid = `job-detail-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <section className="mt-10 border-t border-line pt-8" data-testid={testid}>
      <h2 className="text-xl font-bold">{title}</h2>
      <div className="mt-4">
        <JobDetailContent content={children} sectionTitle={title} />
      </div>
    </section>
  );
}

function ApplyDialog({ job, existing, onDone }) {
  const [open, setOpen] = useState(false);
  const [cover, setCover] = useState(existing?.cover_letter || "");
  const [acc, setAcc] = useState(existing?.accommodation_request || "");
  const [file, setFile] = useState(null);
  const [uploadSession, setUploadSession] = useState(null);
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const send = async (draft) => {
    const er = {};
    if (!draft && !file && !existing?.resume_name) er["apply-resume"] = "Attach your resume or CV (PDF, DOC, DOCX, RTF or TXT, up to 5 MB).";
    if (file && (!file.size || file.size > 5 * 1024 * 1024)) er["apply-resume"] = "Choose a non-empty file of 5 MB or smaller.";
    if (file && !/\.(pdf|doc|docx|rtf|txt)$/i.test(file.name)) er["apply-resume"] = "Choose a PDF, DOC, DOCX, RTF or TXT resume.";
    setErrors(er); setServerErr("");
    if (Object.keys(er).length) return;
    const fd = new FormData();
    fd.append("cover_letter", cover); fd.append("accommodation_request", acc); fd.append("draft", draft ? "true" : "false");
    setBusy(true); setProgress({ percent: 0, label: "Preparing application…" });
    try {
      if (file) {
        const uploadId = await uploadResume(file, job.id, uploadSession, setUploadSession, setProgress);
        fd.append("resume_upload_id", uploadId);
      }
      setProgress({ percent: 98, label: draft ? "Saving your draft…" : "Submitting your application…" });
      const { data } = await api.post(`/jobs/${job.id}/apply`, fd);
      setDone(data); onDone(data); setFile(null); setUploadSession(null);
      setProgress({ percent: 100, label: draft ? "Draft saved. Your resume is stored privately." : "Application submitted." });
      toast.success(draft ? "Draft saved" : "Application submitted");
    } catch (e) {
      setServerErr(errMsg(e));
      setProgress((p) => ({ ...p, label: "Not completed. Your existing application is unchanged; retry or choose another file." }));
      if ([400, 404, 410, 422].includes(e.response?.status)) setUploadSession(null);
    } finally { setBusy(false); }
  };
  return (
    <>
      <button className={btn()} onClick={() => setOpen(true)} data-testid="apply-btn">{existing?.status === "draft" ? "Continue application" : "Apply now"}</button>
      <Dialog open={open} onOpenChange={(value) => { if (!busy) setOpen(value); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl bg-white" data-testid="apply-dialog">
          <DialogHeader><DialogTitle className="text-2xl text-navy">Apply: {job.title}</DialogTitle><DialogDescription>{job.company_name}</DialogDescription></DialogHeader>
          {done && done.status === "submitted" ? (
            <div role="status" className="space-y-4" data-testid="apply-confirmation">
              <p className="flex items-center gap-2 text-lg font-semibold text-green"><CheckCircle2 className="h-6 w-6" aria-hidden="true" />Application submitted</p>
              <p>You can track its status in your dashboard. The employer will update you here on OpenPath.</p>
              <Link to="/dashboard/applications" className={btn()} data-testid="goto-my-applications">View my applications</Link>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); send(false); }} noValidate className="space-y-5">
              <ErrorSummary errors={errors} />
              <div className="space-y-1.5">
                <label htmlFor="apply-resume" className="block font-semibold">Resume / CV <span className="text-err" aria-hidden="true">*</span></label>
                <p id="apply-resume-hint" className="text-sm text-sub">PDF, DOC, DOCX, RTF or TXT, up to 5 MB. Only this employer and you can access it.{existing?.resume_name && ` Current file: ${existing.resume_name}`}</p>
                <input id="apply-resume" type="file" accept=".pdf,.doc,.docx,.rtf,.txt" onChange={(e) => setFile(e.target.files[0] || null)} className="field py-2.5" aria-describedby={`apply-resume-hint${errors["apply-resume"] ? " apply-resume-error" : ""}`} aria-invalid={errors["apply-resume"] ? "true" : undefined} data-testid="apply-resume-input" />
                {errors["apply-resume"] && <p id="apply-resume-error" className="text-sm font-medium text-err">{errors["apply-resume"]}</p>}
              </div>
              <TextArea id="apply-cover" label="Cover letter" rows={6} value={cover} onChange={(e) => setCover(e.target.value)} />
              <TextArea id="apply-accommodation" label="Accommodation request for the recruitment process" hint="Voluntary. Shared only with this employer — for example, extra time for assessments or a captioned video interview." rows={3} value={acc} onChange={(e) => setAcc(e.target.value)} />
              {progress && (
                <div
                  className="rounded-lg border border-line bg-surface p-4"
                  role="status"
                  aria-live="polite"
                  data-testid="resume-upload-progress"
                >
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-semibold text-navy">{progress.label}</span>
                    <span data-testid="resume-upload-percent">{progress.percent}%</span>
                  </div>
                  <div
                    className="mt-3 h-2 overflow-hidden rounded-full bg-line"
                    role="progressbar"
                    aria-label="Resume upload progress"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress.percent}
                  >
                    <div
                      className="h-full bg-blue transition-[width] duration-200"
                      style={{ width: `${progress.percent}%` }}
                    />
                  </div>
                </div>
              )}
              {serverErr && <ErrorBox message={serverErr} />}
              <div className="flex flex-wrap justify-end gap-3">
                <button type="button" className={btn("secondary")} disabled={busy} onClick={() => send(true)} data-testid="apply-save-draft-btn">Save as draft</button>
                <button className={btn()} disabled={busy} data-testid="apply-submit-btn">{busy ? "Submitting…" : "Submit application"}</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const loc = useLocation();
  const { data, loading, error, reload, setData } = useFetch(`/jobs/${id}`);
  if (loading) return <div className="container-x"><Loading /></div>;
  if (error) return <div className="container-x py-10"><ErrorBox message={error} onRetry={reload} /></div>;
  const { job, company, application } = data;
  const closed = job.status !== "published";
  return (
    <div className="container-x py-8">
      <Link to="/jobs" className="link inline-flex min-h-[44px] items-center gap-1"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to jobs</Link>
      <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_340px]">
        <article data-testid="job-detail">
          <div className="flex flex-wrap gap-2">{job.is_sample && <SampleBadge />}{closed && <StatusBadge status={job.status} />}</div>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold">{job.title}</h1>
          <p className="mt-2 text-lg font-medium">{job.company_name}</p>
          <dl className="mt-6 grid gap-4 sm:grid-cols-2 card p-5">
            {[["Location", job.location], ["Work arrangement", label(job.work_mode)], ["Employment type", label(job.employment_type)], ["Experience", job.experience_level ? `${label(job.experience_level)} level` : "Not specified"],
              ["Salary", job.salary_text || money(job.salary_min, job.salary_max, job.salary_currency, " / year") || "Not provided"], ["Sector", job.sector], ["Posted", fmtDate(job.published_at || job.created_at)], ["Application deadline", job.deadline ? fmtDate(job.deadline) : "Not specified"]]
              .filter(([, v]) => v).map(([k, v]) => <div key={k}><dt className="text-sm font-semibold text-sub">{k}</dt><dd className="font-medium">{v}</dd></div>)}
          </dl>
          <DetailSection title="About the role">{job.description}</DetailSection>
          <DetailSection title="Responsibilities">{job.responsibilities}</DetailSection>
          <DetailSection title="Required qualifications">{job.required_qualifications}</DetailSection>
          <DetailSection title="Preferred qualifications">{job.preferred_qualifications}</DetailSection>
          {job.pwd_eligibility_label && <section className="mt-10 border-t border-line pt-8" data-testid="job-pwd-eligibility">
            <h2 className="text-xl font-bold">PwD eligibility</h2>
            <p className="mt-3 font-semibold text-navy">{job.pwd_eligibility_label}</p>
            <p className="mt-2 text-ink">{job.pwd_eligibility_evidence || "The source does not provide role-specific PwD eligibility evidence."}</p>
          </section>}
          {job.skills?.length > 0 && <section className="mt-8"><h2 className="text-xl font-bold">Skills</h2><ul className="mt-3 flex flex-wrap gap-2">{job.skills.map((s) => <li key={s}><Badge tone="grey">{s}</Badge></li>)}</ul></section>}
          <DetailSection title="Recruitment process">{job.recruitment_process}</DetailSection>
          <section className="mt-8 card p-5 bg-surface" aria-labelledby="acc-h">
            <h2 id="acc-h" className="flex items-center gap-2 text-xl font-bold"><Accessibility className="h-5 w-5" aria-hidden="true" />Workplace accessibility & accommodations</h2>
            {job.accessibility_info || job.accommodation_policy ? <>
              {job.accessibility_info && <p className="mt-2 whitespace-pre-line">{job.accessibility_info}</p>}
              {job.accommodation_policy && <p className="mt-2 whitespace-pre-line"><strong>Accommodation policy: </strong>{job.accommodation_policy}</p>}
              <p className="mt-3 text-sm text-sub">
                Provided by the employer. OpenPath has not independently verified this information.
              </p>
            </> : <p className="mt-2 text-sub">The employer has not provided accessibility information for this role. You can ask about accommodations in your application.</p>}
          </section>
        </article>
        <aside className="space-y-4 lg:sticky lg:top-24 self-start" aria-label="Application">
          <div className="card p-5 space-y-3">
            {job.imported ? <>
              <ImportSource item={job} detail />
              {closed ? <p>This listing is not accepting applications.</p> : job.application_url ? <a href={job.application_url} target="_blank" rel="noopener noreferrer" className={`${btn()} w-full`} data-testid="external-job-apply">Apply on source website<span className="sr-only"> via {job.source_name} (opens in new tab)</span></a> : <p>Application link awaiting review.</p>}
            </> : application && application.status !== "draft" && application.status !== "withdrawn" ? (
              <><p className="font-semibold">You applied on {fmtDate(application.created_at)}</p><StatusBadge status={application.status} /><Link to="/dashboard/applications" className={`${btn("secondary")} w-full`}>Track application</Link></>
            ) : closed ? <p className="text-sub">This listing is not accepting applications.</p> : user ? (
              user.id === job.owner_id ? <p className="text-sub">This is your listing.</p> : <ApplyDialog job={job} existing={application} onDone={(a) => setData({ ...data, application: a })} />
            ) : <Link to={`/signin?next=${encodeURIComponent(loc.pathname)}`} className={`${btn()} w-full`} data-testid="signin-to-apply">Sign in to apply</Link>}
            <div className="flex flex-wrap gap-2"><SaveButton kind="job" id={job.id} initial={data.saved} /><ReportButton type="job" id={job.id} /></div>
          </div>
          <div className="card p-5" data-testid="employer-details">
            <h2 className="flex items-center gap-2 text-lg font-bold"><Building2 className="h-5 w-5" aria-hidden="true" />About {company.name || job.company_name}</h2>
            {company.about && <p className="mt-2 text-sub whitespace-pre-line">{company.about}</p>}
            <dl className="mt-3 space-y-1 text-sm">{company.location && <div><dt className="inline font-semibold">Location: </dt><dd className="inline">{company.location}</dd></div>}{company.size && <div><dt className="inline font-semibold">Size: </dt><dd className="inline">{company.size} employees</dd></div>}</dl>
            {company.website && <a href={company.website} target="_blank" rel="noopener noreferrer" className="link mt-2 inline-block">Company website<span className="sr-only"> (opens in new tab)</span></a>}
          </div>
        </aside>
      </div>
    </div>
  );
}
